import * as Sentry from '@sentry/react-native';

/**
 * Global error handler for WakeLock API errors
 * This prevents unhandled promise rejections from crashing the app
 */
export function setupWakeLockErrorHandler() {
  if (typeof window === 'undefined') {
    return; // Not in a browser environment
  }

  // Handle unhandled promise rejections (like WakeLock API errors)
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    
    // Check if this is a WakeLock API error
    if (error && typeof error === 'object' && error.name === 'NotAllowedError') {
      const errorMessage = error.message || 'WakeLock API error';
      
      // Check if the error is related to wake lock
      if (errorMessage.includes('WakeLock') || errorMessage.includes('not visible')) {
        console.warn('WakeLock API error (non-critical):', errorMessage);
        
        // Log to Sentry as a warning, not an error, since this is expected behavior
        Sentry.addBreadcrumb({
          message: 'WakeLock API error - page not visible',
          level: 'warning',
          data: {
            error: errorMessage,
            url: window.location.href,
            visibilityState: document.visibilityState,
          },
        });
        
        // Prevent the error from crashing the app
        event.preventDefault();
        return;
      }
    }
    
    // For other unhandled rejections, let them bubble up normally
    // This ensures we don't suppress legitimate errors
  });

  // Handle general errors that might be related to WakeLock
  window.addEventListener('error', (event) => {
    const error = event.error;
    
    if (error && typeof error === 'object' && error.name === 'NotAllowedError') {
      const errorMessage = error.message || 'WakeLock API error';
      
      if (errorMessage.includes('WakeLock') || errorMessage.includes('not visible')) {
        console.warn('WakeLock API error (non-critical):', errorMessage);
        
        // Log to Sentry as a warning
        Sentry.addBreadcrumb({
          message: 'WakeLock API error - page not visible',
          level: 'warning',
          data: {
            error: errorMessage,
            url: window.location.href,
            visibilityState: document.visibilityState,
          },
        });
        
        // Prevent the error from being thrown
        event.preventDefault();
        return;
      }
    }
  });
}

/**
 * Safe wrapper for WakeLock API requests
 * This function safely attempts to request a wake lock and handles errors gracefully
 */
export async function safeRequestWakeLock(): Promise<WakeLockSentinel | null> {
  if (typeof navigator === 'undefined' || !navigator.wakeLock) {
    return null; // WakeLock API not supported
  }

  try {
    // Check if the page is visible before requesting wake lock
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      console.warn('WakeLock API: Page is not visible, skipping wake lock request');
      return null;
    }

    const wakeLock = await navigator.wakeLock.request('screen');
    return wakeLock;
  } catch (error) {
    if (error instanceof Error && error.name === 'NotAllowedError') {
      console.warn('WakeLock API: Request not allowed (page not visible or user denied):', error.message);
      
      // Log to Sentry as a warning
      Sentry.addBreadcrumb({
        message: 'WakeLock API request not allowed',
        level: 'warning',
        data: {
          error: error.message,
          url: window.location.href,
          visibilityState: document.visibilityState,
        },
      });
    } else {
      console.warn('WakeLock API: Unexpected error:', error);
    }
    return null;
  }
}

// Type definition for WakeLock API
interface WakeLockSentinel {
  released: boolean;
  type: string;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
  removeEventListener(type: 'release', listener: () => void): void;
}

